/**
 * `VITE_AUTH_MODE=local-jwt`: the OIDC `UserManager` surface over a password login.
 *
 * Why a subclass and not a second auth stack: `react-oidc-context`, `useAuth`, the API
 * layer's 401 refresh and the session-expiry safety net all talk to one `UserManager`.
 * Giving them a manager whose token comes from `POST /api/v1/identity/auth/local/login`
 * instead of Cognito leaves every one of those call sites untouched. The token carries
 * the same `custom:*` claims a Cognito ID token does, so `transformOidcUser` reads it as-is.
 *
 * Only the methods the app actually calls are overridden: `getUser`, `removeUser`,
 * `signinSilent` (no refresh token here — the stored token is returned while it is
 * still valid), `signinRedirect` / `signoutRedirect` (go to `/login`), `storeUser`.
 * Everything else inherits the base class's behaviour against a placeholder authority
 * that is never contacted.
 */
import {User, UserManager, WebStorageStateStore} from 'oidc-client-ts';

const STORAGE_KEY = 'matic.localAuth.user';

function readStored(): User | null {
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const user = User.fromStorageString(raw);
    return user.expired ? null : user;
  } catch {
    return null;
  }
}

function decodePayload(idToken: string): Record<string, unknown> {
  const base64Url = idToken.split('.')[1] ?? '';
  const bytes = Uint8Array.from(
    globalThis.atob(base64Url.replaceAll('-', '+').replaceAll('_', '/')),
    (c) => c.codePointAt(0)!
  );
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
}

export class LocalUserManager extends UserManager {
  /** Turn a login response into the `User` every consumer expects and persist it. */
  static userFromIdToken(idToken: string): User {
    const payload = decodePayload(idToken);
    /* eslint-disable @typescript-eslint/naming-convention */
    return new User({
      id_token: idToken,
      access_token: idToken,
      token_type: 'Bearer',
      scope: 'openid',
      expires_at: typeof payload.exp === 'number' ? payload.exp : undefined,
      profile: {
        sub: typeof payload.sub === 'string' ? payload.sub : '',
        iss: typeof payload.iss === 'string' ? payload.iss : 'matic-local',
        aud: 'local',
        exp: Number(payload.exp ?? 0),
        iat: Number(payload.iat ?? 0),
        email: payload.email as string | undefined,
        name: payload.name as string | undefined,
      },
    });
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  constructor(appUrl: string) {
    /* eslint-disable @typescript-eslint/naming-convention */
    super({
      authority: `${appUrl}/local-auth`,
      client_id: 'local',
      redirect_uri: `${appUrl}/`,
      post_logout_redirect_uri: `${appUrl}/login`,
      response_type: 'code',
      scope: 'openid',
      automaticSilentRenew: false,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({store: globalThis.localStorage}),
    });
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  override async getUser(): Promise<User | null> {
    return readStored();
  }

  override async storeUser(user: User | null): Promise<void> {
    if (user) {
      globalThis.localStorage.setItem(STORAGE_KEY, user.toStorageString());
      await this.events.load(user);
    } else {
      globalThis.localStorage.removeItem(STORAGE_KEY);
      await this.events.unload();
    }
  }

  override async removeUser(): Promise<void> {
    globalThis.localStorage.removeItem(STORAGE_KEY);
    await this.events.unload();
  }

  override async signinSilent(): Promise<User | null> {
    return readStored();
  }

  override async signinRedirect(): Promise<void> {
    if (!globalThis.location.pathname.startsWith('/login')) {
      globalThis.location.assign('/login');
    }
  }

  override async signoutRedirect(): Promise<void> {
    await this.removeUser();
    globalThis.location.assign('/login');
  }

  override async signinCallback(): Promise<User | undefined> {
    return readStored() ?? undefined;
  }
}
